import React, { useEffect, useRef, useState } from 'react'
import { Services } from '@/services/services';
import { SuperAdminEditCompanyForm, SuperAdminInsertCompanyForm, CompanyFormData } from '@/components/forms/companies/Company';
import { CurrentSession, SessionService } from '@/services/repository/session';
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout';
import { addServerErrors, onSubmit } from '@/components/forms/common/FormBase';
import { useForm, UseFormReturn } from 'react-hook-form';
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable';
import { useModal } from '@/components/modal/Modal';
import { formatDate } from '@/common/time';
import { isDesktopScreen, isLargeScreen, useResponsiveChange } from '@/common/device';
import { BackButton } from '@/components/layout/NavBar';

export interface SimpleCompany {
  id: string
  email: string
  name: string
  phoneNumber: string
  createdAt: Date
}

export interface SuperAdminInsertPopupProps {
  services: Services,
  session: CurrentSession,
  onClose: () => void
}

export interface SuperAdminEditCompanysPopupProps extends SuperAdminInsertPopupProps {
  id: string | undefined
}

export interface SuperAdminInsertCompanysPopupProps extends SuperAdminInsertPopupProps {
  isOpen: boolean
}

export const SuperAdminEditCompanysPopup = ({ services, session, onClose, id }: SuperAdminEditCompanysPopupProps) => {
  const form = useForm<CompanyFormData>()
  const [data, setData] = useState<CompanyFormData>()

  useEffect(() => {
    if (id === undefined) {
      return
    }
    setData(undefined)
    services.session.fetchWithToken(`/api/superadmin/companies/byid/${id}`).then((r) => r.json()).then((data) => {
      setData(data)
      form.reset(data)
    })
  }, [id]);

  const handle = (data: CompanyFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    return services.session.fetchWithToken(`/api/superadmin/companies/byid/${data.id}/update`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, session.translates, erros)
        return
      }
      onClose()
    })
  }

  return data !== undefined ? <SuperAdminEditCompanyForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    formName='company'
    readOnly={false}
  /> : <div className="spinner"></div>
}

export const SuperAdminInsertCompanysPopup = ({ services, session, onClose, isOpen }: SuperAdminInsertCompanysPopupProps) => {
  const form = useForm<CompanyFormData>()

  const handle = (data: CompanyFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    delete data.id
    return services.session.fetchWithToken(`/api/superadmin/companies/new`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, session.translates, erros)
        return
      }
      form.reset()
      onClose()
    })
  }

  return <SuperAdminInsertCompanyForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    formName='company'
    readOnly={false}
  />
}

export const SuperAdminCompanysPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  // const [edited, setEdited] = useState<string>()
  // const [inserted, setInserted] = useState<boolean>(false)
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()

  useResponsiveChange()

  const tableParams: SearchTableParams<SimpleCompany> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleCompany>> => {
      return services.session.fetchWithToken(`/api/superadmin/companies/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => SuperAdminInsertCompanysPopup({ ...props, services, session }))}><i className='svgfont-add' /></div>
    </>,
    header: () => <>
      {isDesktopScreen() && <div className='hcol '>{session.translates.for('elements.table.companies.createdAt')}</div>}
      {isDesktopScreen() && <div className='hcol'>{session.translates.for('elements.table.companies.email')}</div>}
      {isLargeScreen() && <div className='hcol large-only'>{session.translates.for('elements.table.companies.phoneNumber')}</div>}
      <div className='hcol'>{session.translates.for('elements.table.companies.name')}</div>
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      {isDesktopScreen() && <div className='col'>{formatDate(new Date(row.createdAt))}</div>}
      {isDesktopScreen() && <div className='col'>{row.email}</div>}
      {isLargeScreen() && <div className='col'>{row.phoneNumber}</div>}
      <div className='col'>{row.name}</div>
      <div className='col icocol' onClick={() => openModal((props) => SuperAdminEditCompanysPopup({ ...props, id: row.id, services, session }))}>
        <i className='svgfont-edit' />
      </div>
    </>,
    translates: session.translates
  }

  return <LoggedInPageLayout pageName='superadmin-companies' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}
